/**
 * agreement-terms-citation.ts — reading a booklet's identity out of the page being signed.
 *
 * A Schedule is half an agreement. The other half is a Terms booklet nobody signs, which
 * clause 3.1 incorporates by NAME, VERSION, FORM CODE and the first sixteen characters of
 * its SHA-256. This module turns that sentence back into the three values a booklet can be
 * fetched and checked with.
 *
 * ── WHY THE PROSE IS THE LINK, AND NOT A FOREIGN KEY ──────────────────────────
 * There is no column joining a document to its Terms row. `agreement_documents` carries
 * `template_id` and nothing else; `agreement_templates` carries no `terms_id`. The only
 * machine-resolvable link between an instrument and the booklet it incorporates is the
 * citation printed inside the frozen body — which is the right place for it, because the
 * citation is what the SIGNATURE covers. A foreign key could be repointed after the fact;
 * these sixteen characters are sealed under the signer's own hash.
 *
 * ── THE SENTENCE IS A GUARANTEE, NOT A CONVENTION ─────────────────────────────
 * `20260817010000_nothing_is_signed_so_the_registry_starts_at_version_one.sql` composes the
 * citation from the terms row itself —
 *
 *     'version ' || version || ' (form ' || form_code || '), SHA-256 checksum beginning '
 *     || left(body_sha256, 16)
 *
 * — splices it into the schedule body, and then RAISES unless `position(citation IN body)`
 * is non-zero (:2599-2609). Separately it refuses any body naming the form code more than
 * once (:2582-2591). So on a published v1 body this pattern matches, matches once, and
 * matches values that came off the row. The regex is reading a contract, not guessing.
 *
 * ── EVERY FAILURE IS `null` ───────────────────────────────────────────────────
 * A witness body, a deposit body, a pre-v1 body still sitting on staging, and a stray
 * hex-shaped run all answer the same way: null, never a throw. The caller's job is to show
 * the booklet when there is one and to carry on when there is not — a parser that threw
 * would turn "this instrument incorporates nothing" into a broken ceremony.
 */

export interface TermsCitation {
    /** `PL-HP-TERMS`, `PL-ADV-TERMS` — the publication handle, half of the row's key. */
    formCode: string;
    /** The published version this Schedule incorporates. The other half of the key. */
    version: number;
    /** The first sixteen characters of the booklet's SHA-256, exactly as the page prints. */
    sha16: string;
    /**
     * The booklet's name as the CITED SENTENCE calls it ("Primelane Hire Purchase Terms").
     *
     * Nullable, and deliberately not load-bearing: only the three values above are pinned by
     * the migration's byte-for-byte check, so a future rewording of the surrounding prose
     * must cost a nicer label and never the citation itself. A caller with a resolved row
     * should prefer that row's `title`; this is what is left when nothing resolves.
     */
    title: string | null;
}

/**
 * The citation, with the incorporating clause's own lead-in as an OPTIONAL prefix.
 *
 * The required half is exactly the string the migration composes and proves. The optional
 * half buys the booklet's name for a screen that could not reach the row, and being optional
 * it can never be the reason a citation fails to parse.
 *
 * `(?![0-9a-f])` is not decoration: without it a body carrying a FULL 64-character digest
 * would silently yield its first sixteen characters as though they were the citation's
 * short form. Sixteen followed by a seventeenth hex digit is a different sentence, and a
 * different sentence is not this one.
 */
const CITATION =
    /(?:incorporates the ([^,\n]{1,80}?), )?version (\d{1,4}) \(form ([A-Z][A-Z0-9-]{1,31})\), SHA-256 checksum beginning ([0-9a-f]{16})(?![0-9a-f])/g;

/**
 * Clause 3.1's three values, or null.
 *
 * TWO DISTINCT CITATIONS ALSO ANSWER NULL. The migration forbids the shape, so it should be
 * unreachable — but "which booklet does this page incorporate" is a question with exactly one
 * right answer, and a page that gives two has not answered it. Showing the first would put a
 * booklet under a signature on the strength of a coin toss.
 */
export function parseTermsCitation(md: string | null | undefined): TermsCitation | null {
    if (typeof md !== 'string' || md.length === 0) return null;

    // A `g` regex carries `lastIndex` between calls, so the pattern is re-created per parse
    // rather than shared as module state that a second caller would resume mid-string.
    const pattern = new RegExp(CITATION.source, 'g');
    const found = new Map<string, TermsCitation>();

    for (let match = pattern.exec(md); match !== null; match = pattern.exec(md)) {
        const version = Number(match[2]);
        if (!Number.isInteger(version) || version <= 0) continue;
        const citation: TermsCitation = {
            formCode: match[3],
            version,
            sha16: match[4],
            title: match[1] ? match[1].trim() : null,
        };
        found.set(`${citation.formCode}/${citation.version}/${citation.sha16}`, citation);
        if (found.size > 1) return null;
    }

    return found.size === 1 ? [...found.values()][0] : null;
}

/** How the citation reads on screen when it is all a signer can be shown. */
export function termsCitationLabel(citation: TermsCitation): string {
    return `version ${citation.version} · form ${citation.formCode} · checksum begins ${citation.sha16}`;
}

/**
 * The address the filed booklet PDF is served from, or null.
 *
 * ── COMPOSED OFF `storage_path`, NEVER OFF THE CITATION ───────────────────────
 * `agreement_terms.storage_path` is `GENERATED ALWAYS AS (form_code || '/v' || version ||
 * '.pdf')` and fleet-backend is explicit that the address is READ OFF THE ROW rather than
 * spelled a second time by a caller (`generate-agreement/terms.ts:10-19`). Building
 * `<formCode>/v<version>.pdf` here from a parsed citation would be that second spelling, and
 * the day the generated expression changes it is this app that files the mismatch. So the
 * argument is the row's own value — which also means no row, no link, which is the correct
 * shape: an unresolved booklet has no PDF to offer.
 *
 * The bucket is `public = true` and `public_read_agreement_terms` grants SELECT on its
 * objects to `anon` and `authenticated`, so the object address needs no signing. The path is
 * re-checked anyway before interpolation, for the reason `signatureObjectPath` re-checks
 * `path_scope`: a trusted string spliced into a URL is still a string spliced into a URL.
 */
const STORAGE_PATH_SHAPE = /^[A-Z][A-Z0-9-]{1,31}\/v[0-9]{1,4}\.pdf$/;

const BUCKET_PREFIX = '/storage/v1/object/public/agreement-terms/';

/** `signLink.ts:57`'s reading of the same variable, trailing slashes and all. */
function projectBase(): string {
    return String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '');
}

export function termsBookletUrl(
    storagePath: string | null | undefined,
    baseUrl: string = projectBase(),
): string | null {
    const base = String(baseUrl ?? '').replace(/\/+$/, '');
    if (base.length === 0) return null;
    if (typeof storagePath !== 'string' || !STORAGE_PATH_SHAPE.test(storagePath)) return null;
    return `${base}${BUCKET_PREFIX}${storagePath}`;
}
